import * as THREE from 'three';
import { feedHeightConstraint } from 'bin-3d-feed-volume';

const offsets = [
  // Cardinal directions first
  [1, 0],
  [0, 1],
  [-1, 0],
  [0, -1],
  // Diagonals second!
  [1, 1],
  [1, -1],
  [-1, -1],
  [-1, 1],
];

const cardinalOffsets = [
  [1, 0],
  [0, 1],
  [-1, 0],
  [0, -1],
];

const _isValidWithEmptyNearby = (heightMap, bin, x, y, resolution) => {
  if (!_shouldRenderPoint(heightMap, x, y, bin, resolution)) {
    return false;
  }

  // Is the point an edge? If so then this point is what we want
  for (const innerOffset of offsets) {
    if (!_shouldRenderPoint(heightMap, x + innerOffset[0], y + innerOffset[1], bin, resolution)) {
      return true;
    }
  }

  return false;
};

function shouldRenderHeight (height, max, min, strict = true) {
  return (height !== null && max !== undefined) && (strict === false || height > (min + 0.001));
}

function _shouldRenderPoint(heightMap, x, y, bin, resolution) {
  // Is within bounds of the heightMap square
  if (x < 0 || x > resolution - 1 || y < 0 || y > resolution - 1) {
    return false;
  }

  const feedHeight = heightMap[x][y];
  const { max, min } = feedHeightConstraint(x, y, resolution, bin);
  return shouldRenderHeight(feedHeight, max, min);
}

function _makePoints(heightMap, bin, heightMapResolution, onPointsFound) {
  // loop y, loop x
  for (let y = 0; y < heightMapResolution; y++) {
    for (let x = 0; x < heightMapResolution; x++) {
      // is valid point && has a non-valid point nearby?
      if (!_isValidWithEmptyNearby(heightMap, bin, x, y, heightMapResolution)) {
        continue;
      }

      // loop through offsets
      for (const innerOffset of cardinalOffsets) {
        const offsetPoint = {
          x: x + innerOffset[0],
          y: y + innerOffset[1],
        };
        // offset is valid && has non-valid-point nearby?
        if (_shouldRenderPoint(heightMap, offsetPoint.x, offsetPoint.y, bin, heightMapResolution)) {
          // make points between this point and x,y
          onPointsFound({ point: { x, y }, offsetPoint });
        }
      }
    }
  }
}

function getBinZConstraintsAtPoint(x, y, heightMap, binGeometry, resolution) {
  const feedHeight = heightMap[x]?.[y];
  if (feedHeight === null || feedHeight === undefined) {
    return {};
  }

  const [binX, binY] = binGeometry.heightMapXYCoordinateToBinCoordinate([x + 0.5, y + 0.5], resolution);
  const [min, max] = binGeometry.container.zIntercepts(binX, binY);
  if (min === undefined || max === undefined) {
    console.error('undefined min & max Z when rendering bin', x, y, binX, binY, feedHeight);
    return {};
  }

  if (!shouldRenderHeight(feedHeight, max, min)) {
    return {};
  }

  return { max, min: min, shouldRender: true };
}

export function generateVolume(heightMap, bin, binGeometry, resolution, { yellowDarkMaterial }) {
  const baseZ = -bin.dimensions.totalHeight;
  const positions = [];

  _makePoints(heightMap, bin, resolution, ({ point, offsetPoint }) => {
    const pointFeedHeight = heightMap[point.x][point.y];
    const { min: pointMin, shouldRender: shouldRenderPoint } = getBinZConstraintsAtPoint(point.x, point.y, heightMap, binGeometry, resolution);
    if (!shouldRenderPoint) {
      return;
    }

    const offsetFeedHeight = heightMap[offsetPoint.x][offsetPoint.y];
    const { min: offsetMin, shouldRender: shouldRenderOffset } = getBinZConstraintsAtPoint(offsetPoint.x, offsetPoint.y, heightMap, binGeometry, resolution);
    if (!shouldRenderOffset) {
      return;
    }

    positions.push(
      point.x, point.y, baseZ + pointFeedHeight,
      offsetPoint.x, offsetPoint.y, baseZ + offsetFeedHeight,
      point.x, point.y, baseZ + pointMin,
    );

    positions.push(
      offsetPoint.x, offsetPoint.y, baseZ + offsetFeedHeight,
      offsetPoint.x, offsetPoint.y, baseZ + offsetMin,
      point.x, point.y, baseZ + pointMin,
    );
  });

  const buffGeometry = new THREE.BufferGeometry();
  buffGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
  buffGeometry.computeVertexNormals();
  return new THREE.Mesh(buffGeometry, yellowDarkMaterial);
}

export function generateSurface(heightMap, bin, binGeometry, resolution, { yellowMaterial, yellowDarkBacksideMaterial, yellowDarkMaterial }, renderTopHeight = true) {
  const baseZ = -bin.dimensions.totalHeight;
  const positions = [];
  let extents;

  for (let y = 0; y < resolution; y++) {
    for (let x = 0; x < resolution; x++) {
      const feedHeight = heightMap[x][y];
      const { min, shouldRender } = getBinZConstraintsAtPoint(x, y, heightMap, binGeometry, resolution);
      if (!shouldRender) {
        continue;
      }

      if (!extents) {
        extents = {
          x: { min: x, max: x },
          y: { min: y, max: y },
        };
      } else {
        extents.x.min = Math.min(extents.x.min, x);
        extents.x.max = Math.max(extents.x.max, x);

        extents.y.min = Math.min(extents.y.min, y);
        extents.y.max = Math.max(extents.y.max, y);
      }

      // render right & down
      const rightHeight = x + 1 < resolution ? heightMap[x + 1][y] : null;
      const { min: minRight, shouldRender: shouldRenderRight } = getBinZConstraintsAtPoint(x + 1, y, heightMap, binGeometry, resolution);

      const downHeight = y + 1 < resolution ? heightMap[x][y + 1] : null;
      const { min: minDown, shouldRender: shouldRenderDown } = getBinZConstraintsAtPoint(x, y + 1, heightMap, binGeometry, resolution);
      if (shouldRenderRight && shouldRenderDown) {
        positions.push(
          x, y, baseZ + (renderTopHeight ? feedHeight : min),
          x + 1, y, baseZ + (renderTopHeight ? rightHeight : minRight),
          x, y + 1, baseZ + (renderTopHeight ? downHeight : minDown),
        );
      }

      // Smoothing edges of the circle
      if (!shouldRenderRight && shouldRenderDown) {
        const rightDownHeight = x + 1 < resolution && y + 1 < resolution ? heightMap[x + 1][y + 1] : null;
        const { min: minRightDown, shouldRender: shouldRenderRightDown } = getBinZConstraintsAtPoint(x + 1, y + 1, heightMap, binGeometry, resolution);
        if (shouldRenderRightDown) {
          positions.push(
            x, y, baseZ + (renderTopHeight ? feedHeight : min),
            x + 1, y + 1, baseZ + (renderTopHeight ? rightDownHeight : minRightDown),
            x, y + 1, baseZ + (renderTopHeight ? downHeight : minDown),
          );
        }
      }

      // render left & down
      const leftDownHeight = x > 0 ? heightMap[x - 1][y + 1] : null;
      const { min: minLeftDown, shouldRender: shouldRenderLeftDown } = getBinZConstraintsAtPoint(x - 1, y + 1, heightMap, binGeometry, resolution);
      if (shouldRenderLeftDown && shouldRenderDown) {
        positions.push(
          x, y, baseZ + (renderTopHeight ? feedHeight : min),
          x, y + 1, baseZ + (renderTopHeight ? downHeight : minDown),
          x - 1, y + 1, baseZ + (renderTopHeight ? leftDownHeight : minLeftDown),
        );
      }

      // Smoothing edges of the circle
      if (!shouldRenderLeftDown && shouldRenderDown) {
        const leftHeight = x - 1 >= 0 ? heightMap[x - 1][y] : null;
        const { min: minLeft, shouldRender: shouldRenderLeft } = getBinZConstraintsAtPoint(x - 1, y, heightMap, binGeometry, resolution);
        if (shouldRenderLeft) {
          positions.push(
            x, y, baseZ + (renderTopHeight ? feedHeight : min),
            x, y + 1, baseZ + (renderTopHeight ? downHeight : minDown),
            x - 1, y, baseZ + (renderTopHeight ? leftHeight : minLeft),
          );
        }
      }
    }
  }

  const buffGeometry = new THREE.BufferGeometry();
  buffGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
  buffGeometry.computeVertexNormals();

  const mesh = new THREE.Mesh(buffGeometry, renderTopHeight ? yellowMaterial : yellowDarkMaterial);
  const meshes = new THREE.Group();
  meshes.add(mesh);
  if (renderTopHeight) {
    const underside = mesh.clone();
    underside.material = yellowDarkBacksideMaterial;
    meshes.add(underside);
  }

  return {
    meshes,
    extents,
  };
}

/**
 * Adjusts the position of the feed based on whether the usable height map has even/uneven usable points along a given axis
 * If an axis has an uneven amount of points, then we must offset the feed mesh by half a pixel
 */
function _computeFeedPositionUsableHeightMapAdjustment(p1, p2, resolution) {
  const usableHeightMapWidth = p2 - p1;
  const usableHeightMapWidthPoints = usableHeightMapWidth + 1;

  const unusedHeightMapWidthPoints = resolution - usableHeightMapWidthPoints;
  const unevenUsableXOffset = unusedHeightMapWidthPoints % 2 != 0;
  if (unevenUsableXOffset) {
    const lesserOffset = p1;
    const greaterOffset = resolution - usableHeightMapWidthPoints - lesserOffset;
    return lesserOffset > greaterOffset ? -0.5 : 0.5;
  }

  return 0;
}

/**
 * Compute the XY scale & transform values for the current bin & height map
 */
export function computeFeedXYTransformValues(bin, binGeometry, resolution) {
  if (bin.dimensions.shape === 'circular') {
    const { radius } = bin.dimensions.body;
    const diameter = radius * 2.0;
    const scaleDown = (1.0 / (resolution / diameter));
    const scaleUp = 1.0 / scaleDown;
    const offset = ((-radius) * scaleUp) + 0.5;
    return {
      offsetX: offset,
      offsetY: offset,
      scaleX: scaleDown,
      scaleY: scaleDown,
    };
  }

  const usableHeightMapCoords = binGeometry.usableHeightMapCoordinates(resolution);

  const usableHeightMapWidth = usableHeightMapCoords.x2 - usableHeightMapCoords.x1;
  const usableHeightMapHeight = usableHeightMapCoords.y2 - usableHeightMapCoords.y1;

  const additionalXOffset = _computeFeedPositionUsableHeightMapAdjustment(usableHeightMapCoords.x1, usableHeightMapCoords.x2, resolution);
  const additionalYOffset = _computeFeedPositionUsableHeightMapAdjustment(usableHeightMapCoords.y1, usableHeightMapCoords.y2, resolution);

  const scaleX = bin.dimensions.body.width / usableHeightMapWidth;
  const scaleY = bin.dimensions.body.depth / usableHeightMapHeight;
  const resolutionWidth = resolution - 1;
  const baseOffset = -resolutionWidth / 2; // This will center the heightMap with the bin, in bin space, but does not account for uneven usable heightMaps
  const offsetX = baseOffset + additionalXOffset;
  const offsetY = baseOffset + additionalYOffset;

  return {
    offsetX,
    offsetY,
    scaleX: scaleX,
    scaleY: scaleY,
  };
}
